/**
 * Tokens de cor da marca Gali — fonte unica do valor.
 *
 * O hex da marca aparece UMA vez no repositorio: aqui. Qualquer cor de marca
 * no app sai deste arquivo, em uma das tres formas:
 *
 *   1. Componente Vuetify -> `color="primary"`, `class="text-secondary"`
 *      O tema em `plugins/vuetify.ts` e alimentado por `tokensCor`.
 *   2. CSS de componente  -> `var(--gali-cor-primaria)`
 *      As variaveis sao publicadas em `:root` por `aplicarTokensCor()`,
 *      chamada no boot (`main.ts`) antes do mount.
 *   3. JavaScript         -> `tokensCor.primaria`
 *      Paleta de grafico (ECharts recebe string e nao resolve `var()`) e HTML
 *      gerado em string para impressao (documento novo, sem o `:root` do app).
 *
 * Hex de marca escrito direto em componente e barrado pelo teste
 * `__tests__/tokens.spec.ts`. Tom novo da marca entra aqui, nao no componente.
 */

export const tokensCor = {
  /** Roxo Gali. Acao primaria, cabecalho de tabela, destaque de selecao. */
  primaria: "#6305c6",
  /** Laranja Gali. Cor de apoio: acao secundaria e segunda serie de grafico. */
  secundaria: "#ff6101",
} as const;

export type NomeTokenCor = keyof typeof tokensCor;

/** Variavel CSS publicada em `:root` para cada token. */
export const VARIAVEIS_CSS_COR: Record<NomeTokenCor, string> = {
  primaria: "--gali-cor-primaria",
  secundaria: "--gali-cor-secundaria",
};

/**
 * Publica os tokens como variaveis CSS em `:root`, para o CSS consumir com
 * `var(--gali-cor-primaria)`.
 *
 * Vai em `:root` e nao no tema do Vuetify (`--v-theme-primary`) porque parte do
 * CSS global mira elemento FORA da arvore do app — `::-webkit-scrollbar` pega
 * o `body`, onde a classe `.v-theme--light` nao alcanca.
 */
export function aplicarTokensCor(raiz: HTMLElement = document.documentElement): void {
  for (const nome of Object.keys(VARIAVEIS_CSS_COR) as NomeTokenCor[]) {
    raiz.style.setProperty(VARIAVEIS_CSS_COR[nome], tokensCor[nome]);
  }
}

/**
 * Mesma cor com opacidade, em hex de 8 digitos (`#rrggbbaa`).
 *
 * Existe para paleta de grafico: o ECharts recebe cor como string e nao resolve
 * `var()` nem `color-mix()`. Fora de grafico, use `var()` com `rgba()` no CSS.
 */
export function corComOpacidade(cor: string, opacidade: number): string {
  const limitada = Math.min(Math.max(opacidade, 0), 1);
  const alfa = Math.round(limitada * 255)
    .toString(16)
    .padStart(2, "0");
  return `${cor}${alfa}`;
}
